import { Component, ChangeDetectionStrategy, DestroyRef, Injector, OnInit, inject, runInInjectionContext, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import type { Observable } from 'rxjs';

interface RegionLike { id?: string; name: string; }
type LoadFn<T> = (ctx: { injector: Injector }) => Observable<T>;

const CHIP_BASE =
  'inline-flex items-center gap-2 px-4 py-2 rounded-full border cursor-pointer transition-colors font-alternate text-[13px] font-medium';
const CHIP_SELECTED = 'bg-mty-primary text-white border-mty-primary shadow-sm';
const CHIP_IDLE = 'bg-white border-black/10 text-black/70 hover:border-mty-primary/40 hover:bg-mty-primary/5';

@Component({
  selector: 'app-formly-region-checkboxes',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatIconModule, TranslateModule, FormlyModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-2" [attr.data-field-id]="field.id">
      @if (props.label) {
        <span [class]="labelClass()">
          {{ props.label | translate }}
          @if (props.required) { <span class="text-mty-danger ml-0.5">*</span> }
        </span>
      }
      <div [class]="wrapClass()">
        @for (region of regionNames(); track region) {
          <button
            type="button"
            [class]="chipClass(region)"
            [attr.aria-pressed]="isChecked(region)"
            (click)="toggle(region, !isChecked(region))">
            @if (isChecked(region)) {
              <mat-icon style="font-size:16px;width:16px;height:16px">check</mat-icon>
            }
            {{ region | translate }}
          </button>
        }
      </div>
      @if (isInvalid()) {
        <span class="font-base text-[12px] text-mty-danger">
          {{ 'This field is required' | translate }}
        </span>
      }
    </div>
  `,
})
export class RegionCheckboxesFieldComponent extends FieldType<FieldTypeConfig> implements OnInit {
  private injector = inject(Injector);
  private destroyRef = inject(DestroyRef);

  private loadedRegions = signal<RegionLike[] | null>(null);

  ngOnInit(): void {
    const loadFn = this.props['loadOptions'] as LoadFn<RegionLike[]> | undefined;
    if (typeof loadFn !== 'function') return;
    runInInjectionContext(this.injector, () => {
      loadFn({ injector: this.injector })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((result) => this.loadedRegions.set(result ?? []));
    });
  }

  regionNames(): string[] {
    const mode = this.props['mode'] as 'admin' | 'company';
    if (mode === 'admin') {
      const loaded = this.loadedRegions();
      const source = loaded !== null ? loaded : ((this.props['adminRegions'] as RegionLike[]) ?? []);
      return source.map(r => r.name);
    }
    return ((this.props['companyRegions'] as string[]) ?? []);
  }

  isChecked(region: string): boolean {
    const v = this.formControl.value as string[] | null;
    return Array.isArray(v) && v.includes(region);
  }

  chipClass(region: string): string {
    return `${CHIP_BASE} ${this.isChecked(region) ? CHIP_SELECTED : CHIP_IDLE}`;
  }

  toggle(region: string, checked: boolean): void {
    const v = (this.formControl.value as string[]) ?? [];
    const next = checked ? [...v, region] : v.filter(r => r !== region);
    this.formControl.setValue(next);
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
  }

  isInvalid(): boolean {
    return this.formControl.invalid && (this.formControl.dirty || this.formControl.touched);
  }

  labelClass(): string {
    const base = 'font-alternate text-[12px] font-semibold tracking-wider uppercase';
    return this.isInvalid() ? `${base} text-mty-danger` : `${base} text-black/55`;
  }

  wrapClass(): string {
    const base = 'flex flex-wrap gap-2 rounded-2xl p-2 transition-colors';
    return this.isInvalid() ? `${base} bg-mty-danger/5 ring-1 ring-mty-danger/30` : base;
  }
}
